// slices/platformSettingsSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import PlatformSettingsService from "../services/PlatformSettingsService";

// ==================== HELPER: Error Handler ====================
const handleError = (error) => {
  console.log('Full error object:', JSON.stringify(error, null, 2));
  
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error?.data?.error?.message) {
    errorMessage = error.data.error.message;
  } else if (error?.error && typeof error.error === 'string') {
    errorMessage = error.error;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (error?.response?.data?.error?.message) {
    errorMessage = error.response.data.error.message;
  } else if (error?.response?.data?.error && typeof error.response.data.error === 'string') {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.response?.data?.data?.error?.message) {
    errorMessage = error.response.data.data.error.message;
  } else if (error?.success === false && error?.error) {
    errorMessage = error.error;
  } else if (error?.data && error?.statusCode) {
    if (error.data.error?.message) {
      errorMessage = error.data.error.message;
    } else if (error.data.message) {
      errorMessage = error.data.message;
    }
  } else if (error?.response) {
    if (error.response.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.statusText) {
      errorMessage = error.response.statusText;
    }
  } else if (error?.error) {
    if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.error.message) {
      errorMessage = error.error.message;
    }
  }
  
  console.log('Extracted error message:', errorMessage);
  return errorMessage;
};

// ==================== ASYNC THUNKS ====================

// -------- PUBLIC SETTINGS --------

/**
 * Get public settings (frontend)
 * GET /api/platform-settings/public
 */
export const getPublicSettings = createAsyncThunk(
  'platformSettings/getPublicSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.getPublicSettings();
      console.log("settings=>", JSON.stringify(response))
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get donation statistics (public)
 * GET /api/platform-settings/donation-stats
 */
export const getDonationStatistics = createAsyncThunk(
  'platformSettings/getDonationStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.getDonationStatistics();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get active donation goals summary (public)
 * GET /api/platform-settings/donation-goals/active/summary
 */
export const getActiveGoalsSummary = createAsyncThunk(
  'platformSettings/getActiveGoalsSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.getActiveGoalsSummary();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get donation goal progress (public)
 * GET /api/platform-settings/donation-goals/:index/progress
 */
export const getGoalProgress = createAsyncThunk(
  'platformSettings/getGoalProgress',
  async (index, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.getGoalProgress(index);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Validate donation amount (public)
 * POST /api/platform-settings/validate-donation
 */
export const validateDonationAmount = createAsyncThunk(
  'platformSettings/validateDonationAmount',
  async (amount, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.validateDonationAmount(amount);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Process donation (public)
 * POST /api/platform-settings/process-donation
 */
export const processDonation = createAsyncThunk(
  'platformSettings/processDonation',
  async ({ amount, goalIndex }, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.processDonation(amount, goalIndex);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- ADMIN SETTINGS --------

/**
 * Get all settings (admin only)
 * GET /api/platform-settings
 */
export const getAllSettings = createAsyncThunk(
  'platformSettings/getAllSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.getAllSettings();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- DONATION SETTINGS --------

/**
 * Update donation settings with validation
 * PUT /api/platform-settings/donation
 */
export const updateDonationSettings = createAsyncThunk(
  'platformSettings/updateDonationSettings',
  async (updateData, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.updateDonationSettings(updateData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- PLATFORM INFO --------

/**
 * Update platform info
 * PUT /api/platform-settings/platform-info
 */
export const updatePlatformInfo = createAsyncThunk(
  'platformSettings/updatePlatformInfo',
  async (updateData, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.updatePlatformInfo(updateData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- DONATION GOALS --------

/**
 * Create new donation goal with notification
 * POST /api/platform-settings/donation-goals
 */
export const createDonationGoal = createAsyncThunk(
  'platformSettings/createDonationGoal',
  async (goalData, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.createDonationGoal(goalData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update donation goal with notification
 * PUT /api/platform-settings/donation-goals/:index
 */
export const updateDonationGoal = createAsyncThunk(
  'platformSettings/updateDonationGoal',
  async ({ index, updateData }, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.updateDonationGoal(index, updateData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Delete donation goal with notification
 * DELETE /api/platform-settings/donation-goals/:index
 */
export const deleteDonationGoal = createAsyncThunk(
  'platformSettings/deleteDonationGoal',
  async (index, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.deleteDonationGoal(index);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- FEATURES --------

/**
 * Update features with validation
 * PUT /api/platform-settings/features
 */
export const updateFeatures = createAsyncThunk(
  'platformSettings/updateFeatures',
  async (updateData, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.updateFeatures(updateData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Toggle feature
 * POST /api/platform-settings/toggle-feature/:featureName
 */
export const toggleFeature = createAsyncThunk(
  'platformSettings/toggleFeature',
  async (featureName, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.toggleFeature(featureName);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- NOTIFICATION SETTINGS --------

/**
 * Update notification settings
 * PUT /api/platform-settings/notifications
 */
export const updateNotificationSettings = createAsyncThunk(
  'platformSettings/updateNotificationSettings',
  async (updateData, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.updateNotificationSettings(updateData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- STRIPE CONFIGURATION --------

/**
 * Update stripe configuration
 * PUT /api/platform-settings/stripe
 */
export const updateStripeConfig = createAsyncThunk(
  'platformSettings/updateStripeConfig',
  async (updateData, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.updateStripeConfig(updateData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- SETTINGS MANAGEMENT --------

/**
 * Reset all settings to default
 * POST /api/platform-settings/reset
 */
export const resetToDefaults = createAsyncThunk(
  'platformSettings/resetToDefaults',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.resetToDefaults();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get settings audit log (all changes)
 * GET /api/platform-settings/audit
 */
export const getSettingsAuditLog = createAsyncThunk(
  'platformSettings/getSettingsAuditLog',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.getSettingsAuditLog();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== DONATION MANAGEMENT THUNKS ====================

/**
 * Get donation management settings
 * GET /api/platform-settings/donation-management
 */
export const getDonationManagement = createAsyncThunk(
  'platformSettings/getDonationManagement',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.getDonationManagement();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get featured donations with full data
 * GET /api/platform-settings/donation-management/featured
 */
export const getFeaturedDonations = createAsyncThunk(
  'platformSettings/getFeaturedDonations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.getFeaturedDonations();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get pending review donations
 * GET /api/platform-settings/donation-management/pending-review
 */
export const getPendingReviewDonations = createAsyncThunk(
  'platformSettings/getPendingReviewDonations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.getPendingReviewDonations();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get flagged donations
 * GET /api/platform-settings/donation-management/flagged
 */
export const getFlaggedDonations = createAsyncThunk(
  'platformSettings/getFlaggedDonations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.getFlaggedDonations();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get highlighted donations
 * GET /api/platform-settings/donation-management/highlighted
 */
export const getHighlightedDonations = createAsyncThunk(
  'platformSettings/getHighlightedDonations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.getHighlightedDonations();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== DONATION MANAGEMENT ACTIONS THUNKS ====================

/**
 * Feature a donation (Admin only)
 * POST /api/platform-settings/donation-management/:donationId/feature
 */
export const featureDonation = createAsyncThunk(
  'platformSettings/featureDonation',
  async (donationId, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.featureDonation(donationId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Unfeature a donation (Admin only)
 * DELETE /api/platform-settings/donation-management/:donationId/feature
 */
export const unfeatureDonation = createAsyncThunk(
  'platformSettings/unfeatureDonation',
  async (donationId, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.unfeatureDonation(donationId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Hide a donation from public view (Admin only)
 * POST /api/platform-settings/donation-management/:donationId/hide
 */
export const hideDonation = createAsyncThunk(
  'platformSettings/hideDonation',
  async ({ donationId, reason = '' }, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.hideDonation(donationId, reason);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Show a donation publicly (Admin only)
 * DELETE /api/platform-settings/donation-management/:donationId/hide
 */
export const showDonation = createAsyncThunk(
  'platformSettings/showDonation',
  async (donationId, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.showDonation(donationId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Highlight a donation (Admin only)
 * POST /api/platform-settings/donation-management/:donationId/highlight
 */
export const highlightDonation = createAsyncThunk(
  'platformSettings/highlightDonation',
  async (donationId, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.highlightDonation(donationId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Unhighlight a donation (Admin only)
 * DELETE /api/platform-settings/donation-management/:donationId/highlight
 */
export const unhighlightDonation = createAsyncThunk(
  'platformSettings/unhighlightDonation',
  async (donationId, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.unhighlightDonation(donationId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Flag a donation as fraudulent (Admin only)
 * POST /api/platform-settings/donation-management/:donationId/flag
 */
export const flagDonation = createAsyncThunk(
  'platformSettings/flagDonation',
  async ({ donationId, reason = '' }, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.flagDonation(donationId, reason);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Unflag a donation (Admin only)
 * DELETE /api/platform-settings/donation-management/:donationId/flag
 */
export const unflagDonation = createAsyncThunk(
  'platformSettings/unflagDonation',
  async (donationId, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.unflagDonation(donationId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Override donation status (Admin only)
 * PUT /api/platform-settings/donation-management/:donationId/status
 */
export const overrideDonationStatus = createAsyncThunk(
  'platformSettings/overrideDonationStatus',
  async ({ donationId, status, reason = '' }, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.overrideDonationStatus(donationId, status, reason);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);


// ==================== TERMS OF SERVICE & PRIVACY POLICY THUNKS ====================

/**
 * Get Terms of Service (public)
 * GET /api/platform-settings/terms-of-service
 */
export const getTermsOfService = createAsyncThunk(
  'platformSettings/getTermsOfService',
  async (language = 'en', { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.getTermsOfService(language);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get Privacy Policy (public)
 * GET /api/platform-settings/privacy-policy
 */
export const getPrivacyPolicy = createAsyncThunk(
  'platformSettings/getPrivacyPolicy',
  async (language = 'en', { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.getPrivacyPolicy(language);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get both Terms and Privacy (public)
 * GET /api/platform-settings/terms-and-privacy
 */
export const getTermsAndPrivacy = createAsyncThunk(
  'platformSettings/getTermsAndPrivacy',
  async (language = 'en', { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.getTermsAndPrivacy(language);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Record user acceptance of terms and privacy
 * POST /api/platform-settings/accept-terms
 */
export const acceptTermsAndPrivacy = createAsyncThunk(
  'platformSettings/acceptTermsAndPrivacy',
  async ({ userId, termsVersion, privacyVersion }, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.acceptTermsAndPrivacy(
        userId,
        termsVersion,
        privacyVersion
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Check if user has accepted latest versions
 * GET /api/platform-settings/check-acceptance/:userId
 */
export const checkUserAcceptance = createAsyncThunk(
  'platformSettings/checkUserAcceptance',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.checkUserAcceptance(userId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== TERMS & PRIVACY (ADMIN) THUNKS ====================

/**
 * Update Terms of Service (Admin only)
 * PUT /api/platform-settings/terms-of-service
 */
export const updateTermsOfService = createAsyncThunk(
  'platformSettings/updateTermsOfService',
  async (updateData, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.updateTermsOfService(updateData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update Privacy Policy (Admin only)
 * PUT /api/platform-settings/privacy-policy
 */
export const updatePrivacyPolicy = createAsyncThunk(
  'platformSettings/updatePrivacyPolicy',
  async (updateData, { rejectWithValue }) => {
    try {
      const response = await PlatformSettingsService.updatePrivacyPolicy(updateData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== SLICE ====================

const initialState = {
  // Settings data
  settings: null,
  publicSettings: null,
  donationSettings: null,
  platformInfo: null,
  socialLinks: null,
  features: null,
  notificationSettings: null,
  stripeConfig: null,
   termsOfService: null,
  privacyPolicy: null,
  termsAndPrivacy: null,
  userAcceptance: null,
  // Donation goals
  donationGoals: [],
  activeGoals: [],
  goalProgress: null,
  
  // Statistics
  donationStats: null,
  
  // Donation Management (NEW)
  donationManagement: null,
  featuredDonations: [],
  pendingReviewDonations: [],
  flaggedDonations: [],
  highlightedDonations: [],
  
  // Audit
  auditLog: null,
  
  // Loading states
  loading: false,
  updating: false,
  processing: false,
  loadingStats: false,
  loadingGoals: false,
  loadingAudit: false,
  loadingManagement: false,
  
  // Error states
  error: null,
  updateError: null,
  processError: null,
  managementError: null,
  
  // Success states
  updateSuccess: false,
  processSuccess: false,
  managementSuccess: false,
  
  // UI states
  showDonationModal: false,
  selectedGoalIndex: null,
  isEditing: false,
};

const platformSettingsSlice = createSlice({
  name: 'platformSettings',
  initialState,
  reducers: {
    // Reset states
    resetSettingsState: (state) => {
      state.settings = null;
      state.publicSettings = null;
      state.donationSettings = null;
      state.platformInfo = null;
      state.socialLinks = null;
      state.features = null;
      state.notificationSettings = null;
      state.stripeConfig = null;
      state.donationGoals = [];
      state.activeGoals = [];
      state.goalProgress = null;
      state.donationStats = null;
      state.donationManagement = null;
      state.featuredDonations = [];
      state.pendingReviewDonations = [];
      state.flaggedDonations = [];
      state.highlightedDonations = [];
      state.auditLog = null;
      state.error = null;
      state.updateError = null;
      state.processError = null;
      state.managementError = null;
      state.updateSuccess = false;
      state.processSuccess = false;
      state.managementSuccess = false;
    },
    
    // Clear errors
    clearSettingsErrors: (state) => {
      state.error = null;
      state.updateError = null;
      state.processError = null;
      state.managementError = null;
    },
    
    // Clear success states
    clearSettingsSuccess: (state) => {
      state.updateSuccess = false;
      state.processSuccess = false;
      state.managementSuccess = false;
    },
    
    // Toggle donation modal
    toggleDonationModal: (state, action) => {
      state.showDonationModal = action.payload !== undefined ? action.payload : !state.showDonationModal;
    },
    
    // Set selected goal
    setSelectedGoal: (state, action) => {
      state.selectedGoalIndex = action.payload;
    },
    
    // Set editing mode
    setEditingSettings: (state, action) => {
      state.isEditing = action.payload;
    },
    
    // Update local settings (optimistic update)
    updateLocalSettings: (state, action) => {
      const { section, data } = action.payload;
      
      switch (section) {
        case 'donation':
          state.donationSettings = { ...state.donationSettings, ...data };
          break;
        case 'platformInfo':
          state.platformInfo = { ...state.platformInfo, ...data };
          break;
        case 'features':
          state.features = { ...state.features, ...data };
          break;
        case 'notification':
          state.notificationSettings = { ...state.notificationSettings, ...data };
          break;
        case 'stripe':
          state.stripeConfig = { ...state.stripeConfig, ...data };
          break;
        default:
          break;
      }
      
      // Update main settings object
      if (state.settings) {
        state.settings = { ...state.settings, ...data };
      }
    },
    
    // Add local donation goal
    addLocalGoal: (state, action) => {
      state.donationGoals.push(action.payload);
    },
    
    // Update local donation goal
    updateLocalGoal: (state, action) => {
      const { index, data } = action.payload;
      if (state.donationGoals[index]) {
        state.donationGoals[index] = { ...state.donationGoals[index], ...data };
      }
    },
    
    // Remove local donation goal
    removeLocalGoal: (state, action) => {
      state.donationGoals = state.donationGoals.filter((_, i) => i !== action.payload);
    },
    
    // Update local featured donations
    updateLocalFeaturedDonations: (state, action) => {
      state.featuredDonations = action.payload;
    },
    
    // Update local pending review donations
    updateLocalPendingReview: (state, action) => {
      state.pendingReviewDonations = action.payload;
    },
    
    // Update local flagged donations
    updateLocalFlaggedDonations: (state, action) => {
      state.flaggedDonations = action.payload;
    },
    
    // Update local highlighted donations
    updateLocalHighlightedDonations: (state, action) => {
      state.highlightedDonations = action.payload;
    },
    
    // Clear management error
    clearManagementError: (state) => {
      state.managementError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== GET PUBLIC SETTINGS ==========
      .addCase(getPublicSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
  .addCase(getPublicSettings.fulfilled, (state, action) => {
  state.loading = false;

  const data = action.payload?.data || {};

  // Store complete public settings
  state.publicSettings = data;

  // Store individual public settings sections
  state.donationSettings = data.donationSettings || null;
  state.platformInfo = data.platformInfo || null;
  state.socialLinks = data.socialLinks || null;
  state.features = data.features || null;

  // Store donation goals
  state.donationGoals = Array.isArray(data.donationGoals)
    ? data.donationGoals
    : [];

  // NEW: Store public donation IDs for calculating totals
  state.publicDonationIds = Array.isArray(data.publicDonationIds)
    ? data.publicDonationIds
    : [];

  // Keep active goals synchronized
  state.activeGoals = state.donationGoals.filter(
    goal => goal?.isActive === true
  );

  console.log('🎯 Public donation goals:', state.donationGoals);
  console.log('🎯 Active donation goals:', state.activeGoals);
  console.log('💰 Public donation IDs:', state.publicDonationIds);
})
      .addCase(getPublicSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get public settings';
      })
      
      // ========== GET ALL SETTINGS ==========
      .addCase(getAllSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload.data;
        state.donationSettings = action.payload.data.donationSettings;
        state.platformInfo = action.payload.data.platformInfo;
        state.socialLinks = action.payload.data.socialLinks;
        state.features = action.payload.data.features;
        state.notificationSettings = action.payload.data.notificationSettings;
        state.stripeConfig = action.payload.data.stripeConfig;
        state.donationGoals = action.payload.data.donationGoals || [];
        state.donationManagement = action.payload.data.donationManagement || null;
      })
      .addCase(getAllSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get settings';
      })
      
      // ========== GET DONATION STATISTICS ==========
      .addCase(getDonationStatistics.pending, (state) => {
        state.loadingStats = true;
        state.error = null;
      })
      .addCase(getDonationStatistics.fulfilled, (state, action) => {
        state.loadingStats = false;
        state.donationStats = action.payload.data;
      })
      .addCase(getDonationStatistics.rejected, (state, action) => {
        state.loadingStats = false;
        state.error = action.payload || 'Failed to get donation statistics';
      })
      
      // ========== GET ACTIVE GOALS SUMMARY ==========
      .addCase(getActiveGoalsSummary.pending, (state) => {
        state.loadingGoals = true;
        state.error = null;
      })
      .addCase(getActiveGoalsSummary.fulfilled, (state, action) => {
        state.loadingGoals = false;
        state.activeGoals = action.payload.data || [];
      })
      .addCase(getActiveGoalsSummary.rejected, (state, action) => {
        state.loadingGoals = false;
        state.error = action.payload || 'Failed to get active goals';
      })
      
      // ========== GET GOAL PROGRESS ==========
      .addCase(getGoalProgress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getGoalProgress.fulfilled, (state, action) => {
        state.loading = false;
        state.goalProgress = action.payload.data;
      })
      .addCase(getGoalProgress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get goal progress';
      })
      
      // ========== UPDATE DONATION SETTINGS ==========
      .addCase(updateDonationSettings.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateDonationSettings.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.donationSettings = action.payload.data.donationSettings;
        if (state.settings) {
          state.settings.donationSettings = action.payload.data.donationSettings;
        }
      })
      .addCase(updateDonationSettings.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update donation settings';
        state.updateSuccess = false;
      })
      
      // ========== UPDATE PLATFORM INFO ==========
      .addCase(updatePlatformInfo.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updatePlatformInfo.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.platformInfo = action.payload.data.platformInfo;
        if (state.settings) {
          state.settings.platformInfo = action.payload.data.platformInfo;
        }
      })
      .addCase(updatePlatformInfo.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update platform info';
        state.updateSuccess = false;
      })
      
      // ========== CREATE DONATION GOAL ==========
      .addCase(createDonationGoal.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(createDonationGoal.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.donationGoals.push(action.payload.data);
        state.activeGoals = state.donationGoals.filter(g => g.isActive);
      })
      .addCase(createDonationGoal.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to create donation goal';
        state.updateSuccess = false;
      })
      
      // ========== UPDATE DONATION GOAL ==========
      .addCase(updateDonationGoal.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateDonationGoal.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const { index } = action.meta.arg;
        if (state.donationGoals[index]) {
          state.donationGoals[index] = action.payload.data;
        }
        state.activeGoals = state.donationGoals.filter(g => g.isActive);
      })
      .addCase(updateDonationGoal.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update donation goal';
        state.updateSuccess = false;
      })
      
      // ========== DELETE DONATION GOAL ==========
      .addCase(deleteDonationGoal.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(deleteDonationGoal.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const { index } = action.meta.arg;
        state.donationGoals = state.donationGoals.filter((_, i) => i !== index);
        state.activeGoals = state.donationGoals.filter(g => g.isActive);
      })
      .addCase(deleteDonationGoal.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to delete donation goal';
        state.updateSuccess = false;
      })
      
      // ========== UPDATE FEATURES ==========
      .addCase(updateFeatures.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateFeatures.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.features = action.payload.data;
        if (state.settings) {
          state.settings.features = action.payload.data;
        }
      })
      .addCase(updateFeatures.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update features';
        state.updateSuccess = false;
      })
      
      // ========== TOGGLE FEATURE ==========
      .addCase(toggleFeature.fulfilled, (state, action) => {
        const { feature, enabled } = action.payload.data;
        if (state.features) {
          state.features[feature] = enabled;
        }
        if (state.settings?.features) {
          state.settings.features[feature] = enabled;
        }
      })
      
      // ========== UPDATE NOTIFICATION SETTINGS ==========
      .addCase(updateNotificationSettings.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateNotificationSettings.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.notificationSettings = action.payload.data;
        if (state.settings) {
          state.settings.notificationSettings = action.payload.data;
        }
      })
      .addCase(updateNotificationSettings.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update notification settings';
        state.updateSuccess = false;
      })
      
      // ========== UPDATE STRIPE CONFIG ==========
      .addCase(updateStripeConfig.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateStripeConfig.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.stripeConfig = action.payload.data;
        if (state.settings) {
          state.settings.stripeConfig = action.payload.data;
        }
      })
      .addCase(updateStripeConfig.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update Stripe configuration';
        state.updateSuccess = false;
      })
      
      // ========== PROCESS DONATION ==========
      .addCase(processDonation.pending, (state) => {
        state.processing = true;
        state.processError = null;
        state.processSuccess = false;
      })
      .addCase(processDonation.fulfilled, (state) => {
        state.processing = false;
        state.processSuccess = true;
      })
      .addCase(processDonation.rejected, (state, action) => {
        state.processing = false;
        state.processError = action.payload || 'Failed to process donation';
        state.processSuccess = false;
      })
      
      // ========== RESET TO DEFAULTS ==========
      .addCase(resetToDefaults.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(resetToDefaults.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.settings = action.payload.data;
        state.donationSettings = action.payload.data.donationSettings;
        state.platformInfo = action.payload.data.platformInfo;
        state.socialLinks = action.payload.data.socialLinks;
        state.features = action.payload.data.features;
        state.donationGoals = action.payload.data.donationGoals || [];
        state.donationManagement = action.payload.data.donationManagement || null;
        state.activeGoals = state.donationGoals.filter(g => g.isActive);
      })
      .addCase(resetToDefaults.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to reset settings';
        state.updateSuccess = false;
      })
      
      // ========== GET SETTINGS AUDIT LOG ==========
      .addCase(getSettingsAuditLog.pending, (state) => {
        state.loadingAudit = true;
        state.error = null;
      })
      .addCase(getSettingsAuditLog.fulfilled, (state, action) => {
        state.loadingAudit = false;
        state.auditLog = action.payload.data;
      })
      .addCase(getSettingsAuditLog.rejected, (state, action) => {
        state.loadingAudit = false;
        state.error = action.payload || 'Failed to get audit log';
      })

      // ========== DONATION MANAGEMENT - GET ==========
      .addCase(getDonationManagement.pending, (state) => {
        state.loadingManagement = true;
        state.managementError = null;
      })
      .addCase(getDonationManagement.fulfilled, (state, action) => {
        state.loadingManagement = false;
        state.donationManagement = action.payload.data;
      })
      .addCase(getDonationManagement.rejected, (state, action) => {
        state.loadingManagement = false;
        state.managementError = action.payload || 'Failed to get donation management';
      })

      // ========== GET FEATURED DONATIONS ==========
      .addCase(getFeaturedDonations.pending, (state) => {
        state.loadingManagement = true;
        state.managementError = null;
      })
      .addCase(getFeaturedDonations.fulfilled, (state, action) => {
        state.loadingManagement = false;
        state.featuredDonations = action.payload.data || [];
      })
      .addCase(getFeaturedDonations.rejected, (state, action) => {
        state.loadingManagement = false;
        state.managementError = action.payload || 'Failed to get featured donations';
      })

      // ========== GET PENDING REVIEW DONATIONS ==========
      .addCase(getPendingReviewDonations.pending, (state) => {
        state.loadingManagement = true;
        state.managementError = null;
      })
      .addCase(getPendingReviewDonations.fulfilled, (state, action) => {
        state.loadingManagement = false;
        state.pendingReviewDonations = action.payload.data || [];
      })
      .addCase(getPendingReviewDonations.rejected, (state, action) => {
        state.loadingManagement = false;
        state.managementError = action.payload || 'Failed to get pending review donations';
      })

      // ========== GET FLAGGED DONATIONS ==========
      .addCase(getFlaggedDonations.pending, (state) => {
        state.loadingManagement = true;
        state.managementError = null;
      })
      .addCase(getFlaggedDonations.fulfilled, (state, action) => {
        state.loadingManagement = false;
        state.flaggedDonations = action.payload.data || [];
      })
      .addCase(getFlaggedDonations.rejected, (state, action) => {
        state.loadingManagement = false;
        state.managementError = action.payload || 'Failed to get flagged donations';
      })

      // ========== GET HIGHLIGHTED DONATIONS ==========
      .addCase(getHighlightedDonations.pending, (state) => {
        state.loadingManagement = true;
        state.managementError = null;
      })
      .addCase(getHighlightedDonations.fulfilled, (state, action) => {
        state.loadingManagement = false;
        state.highlightedDonations = action.payload.data || [];
      })
      .addCase(getHighlightedDonations.rejected, (state, action) => {
        state.loadingManagement = false;
        state.managementError = action.payload || 'Failed to get highlighted donations';
      })

      // ========== FEATURE DONATION ==========
      .addCase(featureDonation.pending, (state) => {
        state.updating = true;
        state.managementError = null;
        state.managementSuccess = false;
      })
      .addCase(featureDonation.fulfilled, (state, action) => {
        state.updating = false;
        state.managementSuccess = true;
        // Refresh featured donations after action
      })
      .addCase(featureDonation.rejected, (state, action) => {
        state.updating = false;
        state.managementError = action.payload || 'Failed to feature donation';
        state.managementSuccess = false;
      })

      // ========== UNFEATURE DONATION ==========
      .addCase(unfeatureDonation.pending, (state) => {
        state.updating = true;
        state.managementError = null;
        state.managementSuccess = false;
      })
      .addCase(unfeatureDonation.fulfilled, (state, action) => {
        state.updating = false;
        state.managementSuccess = true;
      })
      .addCase(unfeatureDonation.rejected, (state, action) => {
        state.updating = false;
        state.managementError = action.payload || 'Failed to unfeature donation';
        state.managementSuccess = false;
      })

      // ========== HIDE DONATION ==========
      .addCase(hideDonation.pending, (state) => {
        state.updating = true;
        state.managementError = null;
        state.managementSuccess = false;
      })
      .addCase(hideDonation.fulfilled, (state, action) => {
        state.updating = false;
        state.managementSuccess = true;
      })
      .addCase(hideDonation.rejected, (state, action) => {
        state.updating = false;
        state.managementError = action.payload || 'Failed to hide donation';
        state.managementSuccess = false;
      })

      // ========== SHOW DONATION ==========
      .addCase(showDonation.pending, (state) => {
        state.updating = true;
        state.managementError = null;
        state.managementSuccess = false;
      })
      .addCase(showDonation.fulfilled, (state, action) => {
        state.updating = false;
        state.managementSuccess = true;
      })
      .addCase(showDonation.rejected, (state, action) => {
        state.updating = false;
        state.managementError = action.payload || 'Failed to show donation';
        state.managementSuccess = false;
      })

      // ========== HIGHLIGHT DONATION ==========
      .addCase(highlightDonation.pending, (state) => {
        state.updating = true;
        state.managementError = null;
        state.managementSuccess = false;
      })
      .addCase(highlightDonation.fulfilled, (state, action) => {
        state.updating = false;
        state.managementSuccess = true;
      })
      .addCase(highlightDonation.rejected, (state, action) => {
        state.updating = false;
        state.managementError = action.payload || 'Failed to highlight donation';
        state.managementSuccess = false;
      })

      // ========== UNHIGHLIGHT DONATION ==========
      .addCase(unhighlightDonation.pending, (state) => {
        state.updating = true;
        state.managementError = null;
        state.managementSuccess = false;
      })
      .addCase(unhighlightDonation.fulfilled, (state, action) => {
        state.updating = false;
        state.managementSuccess = true;
      })
      .addCase(unhighlightDonation.rejected, (state, action) => {
        state.updating = false;
        state.managementError = action.payload || 'Failed to unhighlight donation';
        state.managementSuccess = false;
      })

      // ========== FLAG DONATION ==========
      .addCase(flagDonation.pending, (state) => {
        state.updating = true;
        state.managementError = null;
        state.managementSuccess = false;
      })
      .addCase(flagDonation.fulfilled, (state, action) => {
        state.updating = false;
        state.managementSuccess = true;
      })
      .addCase(flagDonation.rejected, (state, action) => {
        state.updating = false;
        state.managementError = action.payload || 'Failed to flag donation';
        state.managementSuccess = false;
      })

      // ========== UNFLAG DONATION ==========
      .addCase(unflagDonation.pending, (state) => {
        state.updating = true;
        state.managementError = null;
        state.managementSuccess = false;
      })
      .addCase(unflagDonation.fulfilled, (state, action) => {
        state.updating = false;
        state.managementSuccess = true;
      })
      .addCase(unflagDonation.rejected, (state, action) => {
        state.updating = false;
        state.managementError = action.payload || 'Failed to unflag donation';
        state.managementSuccess = false;
      })

      // ========== OVERRIDE DONATION STATUS ==========
      .addCase(overrideDonationStatus.pending, (state) => {
        state.updating = true;
        state.managementError = null;
        state.managementSuccess = false;
      })
      .addCase(overrideDonationStatus.fulfilled, (state, action) => {
        state.updating = false;
        state.managementSuccess = true;
      })
      .addCase(overrideDonationStatus.rejected, (state, action) => {
        state.updating = false;
        state.managementError = action.payload || 'Failed to override donation status';
        state.managementSuccess = false;
      }).addCase(getTermsOfService.fulfilled, (state, action) => {
        state.loading = false;
        state.termsOfService = action.payload.data;
      })
      .addCase(getTermsOfService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get Terms of Service';
      })
      
      // ========== GET PRIVACY POLICY ==========
      .addCase(getPrivacyPolicy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPrivacyPolicy.fulfilled, (state, action) => {
        state.loading = false;
        state.privacyPolicy = action.payload.data;
      })
      .addCase(getPrivacyPolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get Privacy Policy';
      })
      
      // ========== GET TERMS AND PRIVACY ==========
      .addCase(getTermsAndPrivacy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTermsAndPrivacy.fulfilled, (state, action) => {
        state.loading = false;
        state.termsAndPrivacy = action.payload.data;
        state.termsOfService = action.payload.data.termsOfService;
        state.privacyPolicy = action.payload.data.privacyPolicy;
      })
      .addCase(getTermsAndPrivacy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get Terms and Privacy';
      })
      
      // ========== ACCEPT TERMS AND PRIVACY ==========
      .addCase(acceptTermsAndPrivacy.pending, (state) => {
        state.processing = true;
        state.processError = null;
        state.processSuccess = false;
      })
      .addCase(acceptTermsAndPrivacy.fulfilled, (state, action) => {
        state.processing = false;
        state.processSuccess = true;
        state.userAcceptance = action.payload.data;
      })
      .addCase(acceptTermsAndPrivacy.rejected, (state, action) => {
        state.processing = false;
        state.processError = action.payload || 'Failed to accept Terms and Privacy';
        state.processSuccess = false;
      })
      
      // ========== CHECK USER ACCEPTANCE ==========
      .addCase(checkUserAcceptance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkUserAcceptance.fulfilled, (state, action) => {
        state.loading = false;
        state.userAcceptance = action.payload.data;
      })
      .addCase(checkUserAcceptance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to check user acceptance';
      })
      
      // ========== UPDATE TERMS OF SERVICE (ADMIN) ==========
      .addCase(updateTermsOfService.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateTermsOfService.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.termsOfService = action.payload.data;
        if (state.termsAndPrivacy) {
          state.termsAndPrivacy.termsOfService = action.payload.data;
        }
      })
      .addCase(updateTermsOfService.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update Terms of Service';
        state.updateSuccess = false;
      })
      
      // ========== UPDATE PRIVACY POLICY (ADMIN) ==========
      .addCase(updatePrivacyPolicy.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updatePrivacyPolicy.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.privacyPolicy = action.payload.data;
        if (state.termsAndPrivacy) {
          state.termsAndPrivacy.privacyPolicy = action.payload.data;
        }
      })
      .addCase(updatePrivacyPolicy.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update Privacy Policy';
        state.updateSuccess = false;
      })
  }
});

// ==================== SELECTORS ====================

// Base selector
const selectSettingsState = (state) => state.platformSettings;

// Settings data selectors
export const selectSettings = createSelector(
  selectSettingsState,
  (settings) => settings.settings
);

export const selectPublicSettings = createSelector(
  selectSettingsState,
  (settings) => settings.publicSettings
);

export const selectDonationSettings = createSelector(
  selectSettingsState,
  (settings) => settings.donationSettings
);

export const selectPlatformInfo = createSelector(
  selectSettingsState,
  (settings) => settings.platformInfo
);

export const selectSocialLinks = createSelector(
  selectSettingsState,
  (settings) => settings.socialLinks
);

export const selectFeatures = createSelector(
  selectSettingsState,
  (settings) => settings.features
);

export const selectNotificationSettings = createSelector(
  selectSettingsState,
  (settings) => settings.notificationSettings
);

export const selectStripeConfig = createSelector(
  selectSettingsState,
  (settings) => settings.stripeConfig
);

// Donation goal selectors
export const selectDonationGoals = createSelector(
  selectSettingsState,
  (settings) => settings.donationGoals
);

export const selectActiveGoals = createSelector(
  selectSettingsState,
  (settings) => settings.activeGoals
);

export const selectGoalProgress = createSelector(
  selectSettingsState,
  (settings) => settings.goalProgress
);

// Statistics selectors
export const selectDonationStats = createSelector(
  selectSettingsState,
  (settings) => settings.donationStats
);

// Donation Management selectors (NEW)
export const selectDonationManagement = createSelector(
  selectSettingsState,
  (settings) => settings.donationManagement
);

export const selectFeaturedDonations = createSelector(
  selectSettingsState,
  (settings) => settings.featuredDonations
);

export const selectPendingReviewDonations = createSelector(
  selectSettingsState,
  (settings) => settings.pendingReviewDonations
);

export const selectFlaggedDonations = createSelector(
  selectSettingsState,
  (settings) => settings.flaggedDonations
);

export const selectHighlightedDonations = createSelector(
  selectSettingsState,
  (settings) => settings.highlightedDonations
);

// Audit selectors
export const selectAuditLog = createSelector(
  selectSettingsState,
  (settings) => settings.auditLog
);

// Loading selectors
export const selectSettingsLoading = createSelector(
  selectSettingsState,
  (settings) => settings.loading
);

export const selectSettingsUpdating = createSelector(
  selectSettingsState,
  (settings) => settings.updating
);

export const selectSettingsProcessing = createSelector(
  selectSettingsState,
  (settings) => settings.processing
);

export const selectStatsLoading = createSelector(
  selectSettingsState,
  (settings) => settings.loadingStats
);

export const selectGoalsLoading = createSelector(
  selectSettingsState,
  (settings) => settings.loadingGoals
);

export const selectAuditLoading = createSelector(
  selectSettingsState,
  (settings) => settings.loadingAudit
);

export const selectManagementLoading = createSelector(
  selectSettingsState,
  (settings) => settings.loadingManagement
);

// Error selectors
export const selectSettingsError = createSelector(
  selectSettingsState,
  (settings) => settings.error
);

export const selectUpdateSettingsError = createSelector(
  selectSettingsState,
  (settings) => settings.updateError
);

export const selectProcessSettingsError = createSelector(
  selectSettingsState,
  (settings) => settings.processError
);

export const selectManagementError = createSelector(
  selectSettingsState,
  (settings) => settings.managementError
);

// Success selectors
export const selectSettingsUpdateSuccess = createSelector(
  selectSettingsState,
  (settings) => settings.updateSuccess
);

export const selectSettingsProcessSuccess = createSelector(
  selectSettingsState,
  (settings) => settings.processSuccess
);

export const selectManagementSuccess = createSelector(
  selectSettingsState,
  (settings) => settings.managementSuccess
);

// UI selectors
export const selectShowDonationModal = createSelector(
  selectSettingsState,
  (settings) => settings.showDonationModal
);

export const selectSelectedGoalIndex = createSelector(
  selectSettingsState,
  (settings) => settings.selectedGoalIndex
);

export const selectIsEditingSettings = createSelector(
  selectSettingsState,
  (settings) => settings.isEditing
);

// Derived selectors
export const selectTotalDonations = createSelector(
  selectDonationStats,
  (stats) => stats?.totalDonations || 0
);

export const selectDonationProgress = createSelector(
  selectDonationStats,
  (stats) => parseFloat(stats?.progress || 0)
);

export const selectActiveGoalsCount = createSelector(
  selectActiveGoals,
  (goals) => goals?.length || 0
);

export const selectCompletedGoals = createSelector(
  selectDonationGoals,
  (goals) => goals?.filter(g => g.currentAmount >= g.targetAmount) || []
);

export const selectTotalGoalTarget = createSelector(
  selectActiveGoals,
  (goals) => goals?.reduce((sum, g) => sum + g.targetAmount, 0) || 0
);

export const selectTotalGoalProgress = createSelector(
  [selectActiveGoals],
  (goals) => {
    if (!goals || goals.length === 0) return 0;
    const total = goals.reduce((sum, g) => sum + g.targetAmount, 0);
    const current = goals.reduce((sum, g) => sum + g.currentAmount, 0);
    return total > 0 ? ((current / total) * 100) : 0;
  }
);

export const selectIsDonationEnabled = createSelector(
  selectFeatures,
  (features) => features?.allowPublicDonations || false
);

export const selectShowDonationProgress = createSelector(
  selectFeatures,
  (features) => features?.showDonationProgress || false
);

export const selectAllowAnonymousDonations = createSelector(
  selectFeatures,
  (features) => features?.allowAnonymousDonations || false
);

// Donation Management derived selectors (NEW)
export const selectFeaturedDonationsCount = createSelector(
  selectFeaturedDonations,
  (donations) => donations?.length || 0
);

export const selectPendingReviewCount = createSelector(
  selectPendingReviewDonations,
  (donations) => donations?.length || 0
);

export const selectFlaggedDonationsCount = createSelector(
  selectFlaggedDonations,
  (donations) => donations?.length || 0
);

export const selectHighlightedDonationsCount = createSelector(
  selectHighlightedDonations,
  (donations) => donations?.length || 0
);


// Terms of Service & Privacy Policy selectors (NEW)
export const selectTermsOfService = createSelector(
  selectSettingsState,
  (settings) => settings.termsOfService
);

export const selectPrivacyPolicy = createSelector(
  selectSettingsState,
  (settings) => settings.privacyPolicy
);

export const selectTermsAndPrivacy = createSelector(
  selectSettingsState,
  (settings) => settings.termsAndPrivacy
);

export const selectUserAcceptance = createSelector(
  selectSettingsState,
  (settings) => settings.userAcceptance
);

export const selectHasAcceptedTerms = createSelector(
  selectUserAcceptance,
  (acceptance) => acceptance?.hasAccepted || false
);

export const selectCurrentTermsVersion = createSelector(
  selectTermsOfService,
  (terms) => terms?.version || null
);

export const selectCurrentPrivacyVersion = createSelector(
  selectPrivacyPolicy,
  (privacy) => privacy?.version || null
);

// ==================== EXPORTS ====================

export const {
  resetSettingsState,
  clearSettingsErrors,
  clearSettingsSuccess,
  toggleDonationModal,
  setSelectedGoal,
  setEditingSettings,
  updateLocalSettings,
  addLocalGoal,
  updateLocalGoal,
  removeLocalGoal,
  updateLocalFeaturedDonations,
  updateLocalPendingReview,
  updateLocalFlaggedDonations,
  updateLocalHighlightedDonations,
  clearManagementError,
} = platformSettingsSlice.actions;

export default platformSettingsSlice.reducer;